import type { FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { currencyInput } from "@/shared/utils/currencyInput";
import type { EstateItem } from "@/shared/types/estateTypes";

export interface CreditCardFormValues {
  cardName: string;
  issuingBank: string;
  balance: string;
}

export const EMPTY_CREDIT_CARD_FORM: CreditCardFormValues = {
  cardName: "",
  issuingBank: "",
  balance: "",
};

export interface CreditCardFieldErrors {
  cardName?: string;
}

export function validateCreditCardFields(form: CreditCardFormValues): CreditCardFieldErrors {
  if (!form.cardName.trim()) return { cardName: "Please enter a card name" };
  return {};
}

export function creditCardToItemFields(form: CreditCardFormValues) {
  return {
    item_name: form.cardName.trim(),
    balance: parseFloat(form.balance) || 0,
    ...(form.issuingBank.trim() ? { metadata: { institution: form.issuingBank.trim() } } : {}),
  };
}

export function parseItemToForm(item: EstateItem): CreditCardFormValues {
  return {
    cardName: item.item_name ?? "",
    issuingBank: (item.metadata?.institution as string) ?? "",
    balance: item.balance ? String(item.balance) : "",
  };
}

interface CreditCardFormProps {
  form: CreditCardFormValues;
  onChange: (form: CreditCardFormValues) => void;
  fieldErrors?: CreditCardFieldErrors;
}

export const CreditCardForm: FC<CreditCardFormProps> = ({ form, onChange, fieldErrors }) => {
  const balanceInput = currencyInput(form.balance, (val) => onChange({ ...form, balance: val }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">Card name</Label>
        <Input
          value={form.cardName}
          onChange={(e) => onChange({ ...form, cardName: e.target.value })}
          placeholder="e.g., Chase Sapphire Preferred"
          className={fieldErrors?.cardName ? "border-error" : ""}
        />
        <FieldError message={fieldErrors?.cardName} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Issuing bank <span className="text-tertiary">(optional)</span>
        </Label>
        <Input
          value={form.issuingBank}
          onChange={(e) => onChange({ ...form, issuingBank: e.target.value })}
          placeholder="e.g., Chase, Amex, Discover"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Balance <span className="text-tertiary">(optional)</span>
        </Label>
        <CurrencyInput
          value={balanceInput.displayValue}
          onChange={balanceInput.handleChange}
          placeholder="0.00"
        />
      </div>
    </div>
  );
};
